import React from "react";
import { Agent, useCloneAgent, useDeleteAgent } from "@/lib/agents-store";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Play, Copy, Trash2, Edit3 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface AgentTableProps {
  agents: Agent[];
  onEdit: (agent: Agent) => void;
  onOpenTest: (agent: Agent) => void;
}
export function AgentTable({ agents, onEdit, onOpenTest }: AgentTableProps) {
  const cloneAgent = useCloneAgent();
  const deleteAgent = useDeleteAgent();
  return (
    <div className="rounded-xl border border-white/5 bg-card/50 overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/30">
          <TableRow>
            <TableHead className="w-[300px]">Agent</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Usage</TableHead>
            <TableHead>Success Rate</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => (
            <TableRow key={agent.id} className="hover:bg-muted/20">
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-white/10">
                    <img src={agent.avatarUrl} alt={agent.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-sm leading-none">{agent.displayName}</p>
                    <p className="text-xs text-muted-foreground mt-1 font-mono">{agent.id}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="capitalize text-[10px]">
                  {agent.status}
                </Badge>
              </TableCell>
              <TableCell>
                <span className="text-sm font-medium">{agent.role}</span>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <p className="text-sm font-bold">{agent.metrics.runs} runs</p>
                  <p className="text-[10px] text-muted-foreground">${agent.metrics.avgCost} / run</p>
                </div>
              </TableCell>
              <TableCell>
                <span className={`text-sm font-bold ${agent.metrics.successRate > 95 ? 'text-green-500' : 'text-amber-500'}`}>
                  {agent.metrics.successRate}%
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenTest(agent)}>
                    <Play className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(agent)} className="gap-2"><Edit3 className="w-4 h-4" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => cloneAgent(agent.id)} className="gap-2"><Copy className="w-4 h-4" /> Clone</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => deleteAgent(agent.id)} className="text-destructive gap-2"><Trash2 className="w-4 h-4" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
